import React, { memo, useCallback, useMemo, useState } from 'react';
import { TabHeaderItem, TabsHeader, showConfirmation, showYesNo } from '@modules/common';
import { Button, Badge, CardTitle } from 'reactstrap';
import { ProposalsPageBloc } from './ProposalsPageBloc';
import { useObservable } from '@core/utils/hooks/rxjs';
import { useLoadingState } from '@core/utils/repository/loading_state';
import { useBloc } from '@core/utils/bloc';
import moment from 'moment';
import { TopLoadingBar } from '@core/components/LoadingBar';
import { openAddProposalModal } from '@modules/proposals/AddProposalModal';
import { CBody } from '@modules/common/CBody';
import { GCTable, GCColumn } from '@core/components/GCTable';
import { CompactProposalTableResponse, ProposalId, ProposalStatus } from '@business/entities/proposal';
import { R } from '@core/utils/r';
import { openViewProposal } from '@modules/proposals/ViewProposalModal';
import { ProposalService } from '@business/services/proposal_service';
import { MoreMenu } from '@modules/common/MoreMenu';
import { openResendProposal } from '@modules/proposals/ViewProposalModal/ResendProposalModal';
import { toMoment } from '@core/utils/time';
import { OrgId } from '@business/entities/organization';
import { Formatter } from '@core/utils/formatter';
import { switchMap, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Config } from '@config';
import { useHistory } from '@core/router';
import { openEmailTemplateModal } from '@modules/common/EmailTemplateModal';
import { ContactAPI } from '@business/api/contact';
import { useSettings } from '@core/utils/hooks/misc/useSettings';
import { CommonService } from '@business/services';
import { openGenericInputModal } from '@modules/common/GenericInputModal';

interface ProposalsPageProps {
  orgId: OrgId;
  orgName: string;
  moduleType?: 'organization' | 'deal';
}

export const ProposalsPage = memo((props: ProposalsPageProps) => {
  const { orgId, orgName, moduleType } = props;
  const bloc = useBloc(ProposalsPageBloc, orgId);
  const loading = useLoadingState(bloc.loading);
  const blocProposals = useObservable(bloc.proposals$, []) as CompactProposalTableResponse[];
  const proposals = useMemo(() => blocProposals.filter(p => R.toNumber(p.orgId) === R.toNumber(orgId)), [blocProposals, orgId]);
  const [status, setStatus] = useState<ProposalStatus | 'archived' | null>(null);
  const history = useHistory();
  const [printLoading, setPrintLoading] = useState<boolean>(false);

  // Canada First: proposals are only created from the Deal Activity tab
  const hideSendProposal = useMemo(() => CommonService.isCanadaFirst(), []);

  const onEditProposalTitle = useCallback(
    (proposalId: ProposalId, title: string) => {
      openGenericInputModal({
        title: 'Update Title',
        label: 'Title',
        button: 'Update',
        initialValue: title,
        onSubmit: name => bloc.updateTitle(proposalId, name),
      });
    },
    [bloc],
  );

  const onProposalPrint = useCallback(
    (proposal: CompactProposalTableResponse) => {
      setPrintLoading(true);
      return of(null)
        .pipe(
          switchMap(() => {
            return bloc.printProposal(proposal.id);
          }),
          tap(data => {
            setPrintLoading(false);
            window.open(data.url, '_blank');
          }),
        )
        .subscribe();
    },
    [bloc],
  );

  const tabs: TabHeaderItem[] = useMemo(
    () => [{ title: 'All' }, { title: 'Drafts' }, { title: 'Sent' }, { title: 'Viewed' }, { title: 'Signed' }, { title: 'Archived' }],
    [],
  );

  const settings = useSettings();
  const isOnePageProposalEnabled = useMemo(() => CommonService.isOnePageProposalEnabled(settings), [settings]);

  const onProposalClick = useCallback(
    (row: CompactProposalTableResponse) => {
      if (row.status === ProposalStatus.draft) {
        if (isOnePageProposalEnabled) {
          history.push(`/add-edit-proposal/${row.id}?lastUrl=/organizations/${row.orgId}/proposals`);
        } else {
          openAddProposalModal({ proposalId: row.id, orgId: props.orgId, lastUrl: `/organizations/${row.orgId}/proposals` });
        }
      } else {
        openViewProposal({ proposalId: row.id, lastUrl: `/organizations/${row.orgId}/proposals` });
      }
    },
    [props.orgId, history, isOnePageProposalEnabled],
  );

  const columns: GCColumn<CompactProposalTableResponse>[] = useMemo(
    () => [
      {
        name: 'Title',
        sortField: 'title',
        minWidth: '300px',
        // sortable: true,
        cell: row => {
          return (
            <div className="d-flex flex-row mt-2 mb-2">
              <div className="d-flex flex-row mr-2 mb-2 align-items-center" onClick={() => onProposalClick(row)}>
                <div style={{ position: 'relative' }}>
                  <img
                    style={{
                      width: 80,
                      height: 105,
                      objectFit: 'cover',
                      objectPosition: 'top',
                      borderRadius: 10,
                    }}
                    src={row.image}
                  />
                  <Badge
                    className="d-flex flex-row p-1 align-items-center text-center"
                    style={{
                      position: 'absolute',
                      left: 0,
                      bottom: 0,
                      right: 0,
                      fontSize: 12,
                      borderTopLeftRadius: 0,
                      borderTopRightRadius: 0,
                      borderBottomLeftRadius: 10,
                      borderBottomRightRadius: 10,
                      justifyContent: 'center',
                    }}
                    // color={ProposalService.getStatusColor(row.status, !!row.deletedAt)}
                    color={ProposalService.getStatusColor(row.status)}
                  >
                    {R.upperFirst(row.status)}
                    {/* {row.deletedAt ? 'Archived' : R.upperFirst(row.status)} */}
                  </Badge>
                </div>
              </div>

              <div className="d-flex flex-column justify-content-center">
                <span className={`text-dark font-weight-medium font-size-16`} onClick={() => onProposalClick(row)}>
                  {row.title}
                </span>

                {row.signer ? (
                  <span className="text-muted font-size-12">
                    Sent: {toMoment(row.created).format('MMM, DD YYYY')} - Signed: {moment(row.signer.signed_at).format('MMM, DD YYYY hh:mm A')}
                  </span>
                ) : (
                  <span className="text-muted font-size-12">Sent: {toMoment(row.created).format('MMM, DD YYYY')}</span>
                )}
              </div>
            </div>
          );
        },
      },
      {
        name: 'Amount',
        sortField: 'amount',
        width: '200px',
        cell: row => {
          return (
            <div onClick={() => onProposalClick(row)}>
              <div className="d-flex flex-row mb-1">
                <span className={`text-dark font-weight-medium font-size-14`}>{Formatter.formatCurrency(row.amount, 'USD')}</span>
              </div>
            </div>
          );
        },
      },
      {
        name: 'Status',
        sortField: 'status',
        width: '100px',
        cell: row => (
          <div className="d-flex flex-row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ alignItems: 'center', flexDirection: 'row', display: 'flex' }}>
              <div className="d-flex">
                <Badge className="d-flex flex-row mr-2 p-1 align-items-center" color={ProposalService.getStatusColor(row.status)}>
                  {R.upperFirst(row.status)}
                </Badge>
                <MoreMenu
                  className="float-right"
                  vertical
                  items={[
                    {
                      title: 'Change Name',
                      icon: 'fa fa-edit',
                      onClick: () => onEditProposalTitle(row.id, row.title),
                    },
                    {
                      icon: 'fa fa-copy',
                      title: 'Duplicate Proposal',
                      onClick: () => {
                        if (isOnePageProposalEnabled) {
                          bloc
                            .newDuplicateProposal(row.id)
                            .pipe(
                              tap(newProposal => {
                                history.push(`/add-edit-proposal/${newProposal.id}?lastUrl=/proposals`);
                              }),
                            )
                            .subscribe();
                        } else {
                          openAddProposalModal({ proposalId: row.id, orgId: row.orgId, duplicate: true });
                        }
                      },
                    },
                    {
                      icon: 'fa fa-envelope',
                      title: 'Resend Proposal',
                      onClick: () => openResendProposal({ proposalId: row.id, orgId: row.orgId }),
                    },
                    {
                      icon: 'fas fa-edit',
                      title: 'Edit Proposal',
                      visible: row.canEdit && isOnePageProposalEnabled,
                      onClick: () => {
                        showConfirmation('Edit Proposal', 'Do you want to edit this proposal?')
                          .pipe(tap(() => history.push(`/add-edit-proposal/${row.id}`)))
                          .subscribe();
                      },
                    },
                    {
                      icon: 'fa fa-print',
                      title: 'Print Proposal',
                      onClick: () => onProposalPrint(row),
                      visible: true,
                      alwaysOpen: true,
                      loader: printLoading,
                    },
                    {
                      icon: 'fa fa-archive',
                      className: 'text-danger',
                      title: row.status !== ProposalStatus.archived ? 'Archive' : 'Unarchive',
                      //   visible: !row.deletedAt,
                      onClick: () => {
                        if (row.status === ProposalStatus.archived) {
                          of(null)
                            .pipe(
                              switchMap(() => showYesNo('Unarchive Proposal', `Do you really want to unarchive this proposal?`)),
                              tap(res => {
                                if (res) {
                                  bloc
                                    .unarchiveProposal(row.id)
                                    .pipe(tap(() => bloc.onReload().subscribe()))
                                    .subscribe();
                                }
                              }),
                            )
                            .subscribe();
                        } else {
                          bloc
                            // .deleteProposal(row.id)
                            .updateStatus(row.id, { status: ProposalStatus.archived })
                            .pipe(
                              tap(() => bloc.onReload().subscribe()),
                              switchMap(() =>
                                showConfirmation('Send Email', `Do you want to send email to the client?`, { btnYesLabel: 'Send Email' }),
                              ),
                              switchMap(() => {
                                return openEmailTemplateModal({
                                  emails: row.senderEmails,
                                  messageRequired: true,
                                  subjectRequired: true,
                                }).pipe(
                                  switchMap(data => {
                                    return ContactAPI.sendGenericEmail(data);
                                  }),
                                );
                              }),
                            )
                            .subscribe();
                        }
                      },
                    },
                    {
                      icon: 'fa fa-trash',
                      className: 'text-danger',
                      title: 'Delete Proposal',
                      visible: true,
                      onClick: () => {
                        bloc
                          .hardDeleteProposal(row.id)
                          .pipe(
                            tap(() => bloc.onReload().subscribe()),
                            switchMap(() =>
                              showConfirmation('Send Email', `Do you want to send email to the client?`, { btnYesLabel: 'Send Email' }),
                            ),
                            switchMap(() => {
                              return openEmailTemplateModal({
                                emails: row.senderEmails,
                                messageRequired: true,
                                subjectRequired: true,
                              }).pipe(
                                switchMap(data => {
                                  return ContactAPI.sendGenericEmail(data);
                                }),
                              );
                            }),
                          )
                          .subscribe();
                      },
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        ),
      },
    ],
    [bloc, onProposalClick, history, printLoading, onEditProposalTitle, isOnePageProposalEnabled, onProposalPrint],
  );

  const onTabIndexChange = useCallback((index: number) => {
    if (!index) return setStatus(null);
    if (index === 1) return setStatus(ProposalStatus.draft);
    if (index === 2) return setStatus(ProposalStatus.sent);
    if (index === 3) return setStatus(ProposalStatus.viewed);
    if (index === 4) return setStatus(ProposalStatus.accepted);
    if (index === 5) return setStatus(ProposalStatus.archived);
    // if (index === 5) return setStatus(ProposalStatus.void);
    // if (index === 6) return setStatus(ProposalStatus.cancel);
  }, []);

  const items = useMemo(() => {
    const filtered =
      status && status == ProposalStatus.archived
        ? proposals.filter(p => p.status === ProposalStatus.archived)
        : proposals.filter(p => !status || (p.status === status && !p.deletedAt));
    const resp = R.sortBy(filtered, u => u.created);
    return resp.reverse();
  }, [proposals, status]);

  if (loading.loading) return <TopLoadingBar />;

  return (
    <div className="main_tabs">
      <div className="d-flex justify-content-between align-items-center">
        <CardTitle className="mb-0"></CardTitle>
        <div className="d-flex align-items-center">
          {!hideSendProposal && (
            <Button
              color="primary"
              className="btn btn-primary btn-sm"
              onClick={() => {
                if (isOnePageProposalEnabled) {
                  history.push(`/add-edit-proposal?orgId=${orgId}&orgName=${encodeURIComponent(orgName)}&lastUrl=/organizations/${orgId}/proposals`);
                } else {
                  openAddProposalModal({ orgId, fullOrgResponse: true, lastUrl: `/organizations/${orgId}/proposals` });
                }
              }}
            >
              <i className="fa fa-plus mr-2"></i>
              Send Proposal
            </Button>
          )}
        </div>
      </div>
      <TabsHeader pills tabs={tabs} onChange={onTabIndexChange} />
      <CBody>
        <GCTable
          noTableHead
          customStyles={{
            rows: {
              style: { transition: 'all 0.25s ease-in-out' },
              highlightOnHoverStyle: { backgroundColor: '#556ee620', cursor: 'pointer', borderRadius: 5 },
            },
          }}
          onRowClicked={row => R.noop()}
          highlightOnHover
          pointerOnHover
          noSearch
          striped={false}
          bordered={false}
          noHeader
          sortServer
          columns={columns}
          data={items}
          paginationRowsPerPageOptions={[10, 25, 50, 100, 250, 500]}
          noQueryOnSearch
        />
      </CBody>
    </div>
  );
});
